import { createContext, type ReactNode, useContext, useMemo } from "react";

import {
  type OrderTicketDrawerControllerValue,
  useOrderTicketDrawerController,
} from "./OrderTicketDrawer.controller";
import type {
  OrderTicketDrawerApi,
  WorkspaceEngine,
} from "./OrderTicketDrawer.types";

const OrderTicketDrawerContext =
  createContext<OrderTicketDrawerControllerValue | null>(null);

const useRequiredContext = (): OrderTicketDrawerControllerValue => {
  const value = useContext(OrderTicketDrawerContext);
  if (!value) {
    throw new Error(
      "useOrderTicketDrawer must be used within OrderTicketDrawerProvider",
    );
  }
  return value;
};

export const OrderTicketDrawerProvider = ({
  engine,
  children,
}: {
  engine: WorkspaceEngine;
  children: ReactNode;
}) => {
  const controller = useOrderTicketDrawerController({ engine });
  return (
    <OrderTicketDrawerContext.Provider value={controller}>
      {children}
    </OrderTicketDrawerContext.Provider>
  );
};

export const useOrderTicketDrawer = (): OrderTicketDrawerApi => {
  const value = useRequiredContext();
  return useMemo(
    () => ({
      openOrderTicket: value.openOrderTicket,
      closeOrderTicket: value.closeOrderTicket,
      toggleOrderTicket: value.toggleOrderTicket,
    }),
    [value.closeOrderTicket, value.openOrderTicket, value.toggleOrderTicket],
  );
};

export const useOrderTicketDrawerState = (): OrderTicketDrawerControllerValue =>
  useRequiredContext();
